import { ChevronRight, CircleDollarSign, CreditCard, ExternalLink } from 'lucide-react'
import { useState } from 'react'

import type { RegionId } from '@snootsvet/shared'
import { formatRegionPrice, PROCEDURE_TYPE_LABELS } from '@snootsvet/shared'

import { Badge } from '@/frontend/components/ui/badge'
import { Button } from '@/frontend/components/ui/button'
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/frontend/components/ui/card'
import { PatientMembershipOverdueInvoices } from '@/frontend/features/patient/invoice-list'
import {
  FleaWormingPlanBadge,
  PatientMembershipAddons,
  PatientMembershipPrimaryDetails,
} from '@/frontend/features/patient/patient-membership-details'
import { PatientMembershipDialog } from '@/frontend/features/patient/patient-membership-dialog'
import { useGetPatientInvoices } from '@/frontend/hooks/api/patients/use-get-patient-invoices'
import { useGetPatientSubscription } from '@/frontend/hooks/api/patients/use-get-patient-subscription'
import { cn } from '@/frontend/lib/utils'

/** Inline Stripe link status with edit and open-in-Stripe affordances. */
function StripeStatusRow({ linked, stripeUrl }: { linked: boolean; stripeUrl?: string | null }) {
  return (
    <div className="flex items-center gap-1.5">
      <CreditCard
        className={cn(
          'size-3 shrink-0',
          linked ? 'text-success-accent' : 'text-destructive-accent'
        )}
      />
      <span
        className={cn('font-semibold', linked ? 'text-success-accent' : 'text-destructive-accent')}
      >
        {linked ? 'Stripe linked' : 'Stripe not linked'}
      </span>
      <div className="ml-auto flex items-center gap-0.5 -my-1">
        {stripeUrl && (
          <Button
            variant="ghost"
            size="icon-xs"
            render={<a href={stripeUrl} target="_blank" rel="noopener noreferrer" />}
            aria-label="View in Stripe"
          >
            <ExternalLink className="size-3.5" />
          </Button>
        )}
      </div>
    </div>
  )
}

/** Sidebar card showing the patient's subscription and payment status. */
export function AccountStatusCard({
  patientId,
  memberId,
  memberEmail,
  memberRegion,
}: {
  patientId: string
  memberId: string
  memberEmail: string
  memberRegion: RegionId
}) {
  const { data: subscription, isLoading } = useGetPatientSubscription(patientId)
  const { data: invoices = [] } = useGetPatientInvoices(patientId)
  const [manageOpen, setManageOpen] = useState(false)

  if (!subscription) {
    return (
      <Card>
        <CardHeader className="cursor-not-allowed" aria-disabled>
          <CardTitle>Account Status</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="h-5 w-32 animate-pulse rounded bg-muted" />
          ) : (
            <p className="text-sm text-muted-foreground">Account status is unavailable.</p>
          )}
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card>
        <CardHeader className="cursor-pointer" onClick={() => setManageOpen(true)}>
          <CardTitle>Account Status</CardTitle>
          <CardAction>
            <ChevronRight className="size-4 text-muted-foreground" />
          </CardAction>
        </CardHeader>
        <CardContent className="space-y-3">
          <StripeStatusRow
            linked={!!subscription.stripeSubscriptionId}
            stripeUrl={subscription.stripeSubscriptionUrl}
          />
          {subscription.status ? (
            <div className="space-y-3">
              <PatientMembershipPrimaryDetails
                subscription={subscription}
                pendingUpgradeInvoiceUrl={
                  invoices.find((invoice) => invoice.pendingSubscriptionUpdate)?.dashboardInvoiceUrl
                }
              />
              <PatientMembershipAddons subscription={subscription} />
              {(subscription.hasFleaWormTickPlan || subscription.procedureFees.length > 0) && (
                <div className="flex flex-wrap gap-1.5">
                  <FleaWormingPlanBadge subscription={subscription} memberRegion={memberRegion} />
                  {subscription.procedureFees.map((fee) => (
                    <Badge key={fee.procedure} variant="pending" size="sm">
                      <CircleDollarSign className="size-3" />
                      {PROCEDURE_TYPE_LABELS[fee.procedure]}{' '}
                      {formatRegionPrice(fee.amount, memberRegion)}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <Badge variant="secondary" size="sm">
              No subscription
            </Badge>
          )}
          <PatientMembershipOverdueInvoices
            invoices={invoices}
            patientId={patientId}
            memberEmail={memberEmail}
            memberRegion={memberRegion}
          />
        </CardContent>
      </Card>

      <PatientMembershipDialog
        patientId={patientId}
        memberId={memberId}
        open={manageOpen}
        onOpenChange={setManageOpen}
        memberEmail={memberEmail}
        memberRegion={memberRegion}
        subscription={subscription}
        invoices={invoices}
      />
    </>
  )
}
